import { motion } from "framer-motion";
import { useDeck } from "../../lib/deck-context";
import { PARCELS, SITE_ACRES, SITE_SF } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal } from "../deck/primitives";

const ac = (sf) => (sf / 43560).toFixed(2);
const pct = (sf) => `${((sf / SITE_SF) * 100).toFixed(1)}%`;

const Parcels = () => (
  <div className="grid sm:grid-cols-3 gap-4">
    {PARCELS.map((p, i) => (
      <Card key={p.apn} data-testid={`parcel-${i + 1}`} className="p-6">
        <Eyebrow>Parcel {i + 1}</Eyebrow>
        <div className="mt-4 font-display text-3xl text-ink tabular-nums">{p.acres.toFixed(2)} ac</div>
        <div className="text-xs text-steel mt-1 tabular-nums">≈ {Math.round(p.acres * 43560).toLocaleString()} SF</div>
      </Card>
    ))}
    <Card data-testid="parcel-total" className="p-6 bg-arq border-arq text-white">
      <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/70">Total Subject Site</div>
      <div className="mt-2 font-mono text-lg">2 parcels</div>
      <div className="mt-4 font-display text-3xl tabular-nums">{SITE_ACRES} ac</div>
      <div className="text-xs text-white/75 mt-1 tabular-nums">{SITE_SF.toLocaleString()} SF total lot size</div>
    </Card>
  </div>
);

export const SiteAllocation = () => {
  const { m, inputs } = useDeck();
  const { rows, stalls, ratio, coverage, over } = m.site;
  const reserve = rows.find((r) => r.key === "reserve").sf;
  const kpis = [
    ["Building footprint", `${Math.round(rows[0].sf).toLocaleString()} SF`, `${(coverage * 100).toFixed(1)}% site coverage`],
    ["Rentable area", `${inputs.sf.toLocaleString()} SF`, "Working program"],
    ["Parking", `${stalls} spaces`, `≈ ${ratio.toFixed(2)} per 1,000 SF · underwritten`],
    ["Unallocated land", `${ac(reserve)} ac`, `${pct(reserve)} of site · future phase potential`],
  ];
  return (
    <Reveal delay={0.1} className="mt-14" data-testid="site-allocation">
      <div className="mb-6">
        <Eyebrow>Site Area + Preliminary Land Allocation</Eyebrow>
        <div className="mt-2 font-display text-3xl text-ink">Buildable area vs. parking vs. open space</div>
      </div>
      <Parcels />
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(([l, v, d]) => (
          <Card key={l} className="p-6" data-testid={`site-kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-xs text-steel">{l}</div>
            <div className="mt-2 font-display text-2xl text-ink tabular-nums">{v}</div>
            <div className="mt-1 text-xs text-steel">{d}</div>
          </Card>
        ))}
      </div>
      <Card className="mt-4 p-7">
        <div data-testid="site-allocation-bar" className="flex h-12 w-full overflow-hidden rounded-lg">
          {rows.map((r, i) => (
            <motion.div key={r.key} title={`${r.label} · ${pct(r.sf)}`} style={{ background: r.color }} className="h-full"
              initial={{ width: 0 }} animate={{ width: pct(r.sf) }} transition={{ duration: 0.8, delay: i * 0.06 }} />
          ))}
        </div>
        <div className="mt-6 overflow-x-auto">
          <table data-testid="site-allocation-table" className="data-table w-full min-w-[720px]">
            <thead><tr><th>Use</th><th className="!text-right">Area (SF)</th><th className="!text-right">Acres</th><th className="!text-right">% Site</th><th>Basis</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`site-row-${r.key}`}>
                  <td className="text-ink"><span className="inline-block h-2.5 w-2.5 rounded-sm mr-3 align-middle" style={{ background: r.color }} />{r.label}</td>
                  <td className="text-right tabular-nums text-ink">{Math.round(r.sf).toLocaleString()}</td>
                  <td className="text-right tabular-nums text-char">{ac(r.sf)}</td>
                  <td className="text-right tabular-nums text-steel">{pct(r.sf)}</td>
                  <td className="text-[13px] text-steel">{r.basis}</td>
                </tr>
              ))}
              <tr className="bg-ink-deep text-white hover:!bg-ink-deep">
                <td className="!py-4 font-mono text-xs tracking-[0.16em]">TOTAL SUBJECT SITE</td>
                <td className="!py-4 text-right tabular-nums">{SITE_SF.toLocaleString()}</td>
                <td className="!py-4 text-right tabular-nums">{SITE_ACRES}</td>
                <td className="!py-4 text-right">100%</td>
                <td className="!py-4 text-[13px] text-white/70">Two parcels</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      {over && <Note tone="accent" className="mt-4">Current inputs exceed the available site area — reduce parking spaces, courtyard or rentable area.</Note>}
      <Note testid="site-gis-reconcile" tone="accent" className="mt-4">
        <strong>Acreage reconciliation required:</strong> Riverside County Assessor GIS parcel polygons measure ≈ 2.12 ac and ≈ 5.98 ac for the two parcels, ≈ 8.1 ac / ~353,000 SF combined — versus 12.62 ac / 549,727 SF in the working site data. Confirm by ALTA survey and title before finalizing the site plan.
      </Note>
      <Note testid="site-allocation-note" className="mt-4">
        Preliminary planning allocation for underwriting only — not a site plan. Final buildable area, setbacks, required parking ratio, stormwater and access are subject to the Downtown Murrieta Specific Plan, architect / civil design and City review. Parking spaces (215 underwritten) and courtyard area can be adjusted in Assumptions.
      </Note>
    </Reveal>
  );
};
